import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  Check,
  Edit3,
  FileUp,
  Info,
  Plus,
  Rocket,
  Trash2,
  X
} from 'lucide-react'
import styles from './ProjectAutofillCard.module.scss'
import sm from '../../../../UI/NewSubProjectManager/NewSubProjectManagerV2.module.scss'
import draftPublishStyles from '../DraftPublishButton.module.scss'
import {
  formatPublishProjectAutofillDraftError,
  getProjectAutofillDraft,
  parsePublishProjectAutofillDraftResponse,
  patchProjectAutofillDraft,
  postProjectAutofillDraftFile,
  publishProjectAutofillDraft
} from '../../../../../services/projectAutofillDraftService.ts'
import {
  useProjectAutofillDraftGuardOptional
} from '../../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import { useActionGuard } from '../../../../../hooks/useActionGuard.ts'
import type { ProjectAutofillDraftGuardApi } from '../../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import { profesionalesService } from '../../../../../services/profesionalesService.ts'
import CustomSelect from '../../../../../pages/ProjectDocumentation/components/CustomSelect.tsx'
import PlacesAutocomplete from '../../../../UI/PlacesAutocomplete/PlacesAutocomplete.tsx'
import ModalOverlay from '../../../../UI/ModalOverlay/ModalOverlay.tsx'
import RubroDocumentationBlock from './RubroDocumentationBlock.tsx'
import ProjectAutofillRubroDetailModal from './ProjectAutofillRubroDetailModal.tsx'
import PublishedPrivateProjectCard from './PublishedPrivateProjectCard.tsx'
import type {
  CommittedDraftSnapshot,
  DocumentationSlots,
  DraftMetadataFileEntry,
  DraftValidationShape,
  ProjectAutofillPayload,
  RubroRow,
  SkillOption
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  clampIsoDateToMax,
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotsHavePages,
  EMPTY_REQUIRED_FIELDS,
  extractDraftMetadataFilesFromResponse,
  getEffectiveDocumentationPageCount,
  humanizeDraftStatus,
  mapRubrosToDraftPayload,
  rubroHasValidSelectedSkill,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizePageList,
  pickNewFileIdAfterUpload,
  getRubroTempIdsWithDocumentationPageIssues,
  rubroNeedsMasterPdfForCrop,
  serializeRubrosSnapshot,
  shallowMergeDraftMetadataForPatch,
  validateRubrosDocumentationPages,
  validationBundleFromPatchResponse
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  computeProjectAutofillEditorUiState,
  matchValidationMessagesToRubroTempIds,
  mergeProjectAutofillDraftValidation,
  warningMatchesSuggestedDescription,
  warningMatchesSuggestedDocumentation,
  warningMatchesSuggestedEndDate,
  warningMatchesSuggestedName
} from '../../../../../utils/conversationalMode/projectAutofillEditorValidation.ts'

const PUBLISHED_RUBRO_EDIT_HINT =
  'Para editar rubros o datos de la obra publicada, usá «Ir a la obra» y gestioná todo desde la ficha.'

function rubroKey(sp: RubroRow, idx: number): string {
  return sp.tempId || `sp-${idx}`
}

function isRubroFuzzyKind(kind?: string): boolean {
  return String(kind || '').toLowerCase().includes('fuzzy')
}

function isRubroUnresolvedKind(kind?: string): boolean {
  return String(kind || '').toLowerCase() === 'unresolved'
}

function rubroHasDocumentationToReset(r: RubroRow): boolean {
  const slots = normalizeDocumentationSlots(r.documentationSlots ?? {})
  if (documentationSlotsHavePages(slots)) return true
  if (Array.isArray(r.sourcePages) && r.sourcePages.length > 0) return true
  const ids = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
  return DOCUMENTATION_SLOT_KEYS.some((k) => {
    const v = ids[k]
    return typeof v === 'string' && v.trim().length > 0
  })
}

const PENDING_INPUT_STYLE: React.CSSProperties = {
  borderColor: '#cbd5e1',
  boxShadow: 'inset 0 0 0 2px #ea580c',
  borderRadius: 10
}

const ProjectAutofillCard: React.FC<{
  payload: ProjectAutofillPayload
  embedLayout?: 'default' | 'createObraDesktop'
  onRequestChangeSourceDocument?: () => void
}> = ({ payload, embedLayout = 'default', onRequestChangeSourceDocument }) => {
  const isCreateObraShell = embedLayout === 'createObraDesktop'
  const draftGuardCtx = useProjectAutofillDraftGuardOptional()
  const guardSetApi = draftGuardCtx?.setProjectAutofillDraftGuardApi
  const guardBumpUi = draftGuardCtx?.bumpDraftUi
  const { checkAccess } = useActionGuard()
  const cachedSkills = useSelector((state: any) => state.register?.skillsList || [])
  const cachedSkillsRef = useRef(cachedSkills)
  cachedSkillsRef.current = cachedSkills
  const initialProject = payload.draft?.project || {}
  const [name, setName] = useState(initialProject.name || '')
  const [description, setDescription] = useState(initialProject.description || '')
  const [zone, setZone] = useState(initialProject.zone || '')
  const [startDate, setStartDate] = useState(initialProject.startDate || '')
  const [endDate, setEndDate] = useState(initialProject.endDate || '')
  const [rubros, setRubros] = useState<RubroRow[]>([])
  const [expandedRubros, setExpandedRubros] = useState<Record<string, boolean>>({})
  const [skillOptions, setSkillOptions] = useState<SkillOption[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'ok' | 'error'>('idle')
  const [fuzzyConfirmByRubro, setFuzzyConfirmByRubro] = useState<Record<string, boolean>>({})
  const [patchedDraftSync, setPatchedDraftSync] = useState<{
    validation?: DraftValidationShape
    collectedData?: ProjectAutofillPayload['collectedData']
  } | null>(null)
  const [metadataFilesOverride, setMetadataFilesOverride] = useState<DraftMetadataFileEntry[] | null>(null)
  const [uploadingSlotKey, setUploadingSlotKey] = useState<string | null>(null)
  const [pendingRubroSkillChange, setPendingRubroSkillChange] = useState<{
    tempId: string
    skill: SkillOption
  } | null>(null)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [publishSuccess, setPublishSuccess] = useState<{
    proyectoId: string
    obraName: string
    subproyectoCount: number
  } | null>(null)
  const [createObraRubroModalRid, setCreateObraRubroModalRid] = useState<string | null>(null)
  const [createObraPdfInfoOpen, setCreateObraPdfInfoOpen] = useState(false)
  const createObraPdfInfoRef = useRef<HTMLDivElement>(null)
  const committedRef = useRef<CommittedDraftSnapshot | null>(null)

  useEffect(() => {
    if (!createObraPdfInfoOpen) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (createObraPdfInfoRef.current && !createObraPdfInfoRef.current.contains(e.target as Node)) {
        setCreateObraPdfInfoOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCreateObraPdfInfoOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [createObraPdfInfoOpen])

  useEffect(() => {
    const nextName = payload.draft?.project?.name || ''
    const nextDescription = payload.draft?.project?.description || ''
    const nextZone = payload.draft?.project?.zone || ''
    const nextStart = payload.draft?.project?.startDate || ''
    const nextEnd = payload.draft?.project?.endDate || ''
    setName(nextName)
    setDescription(nextDescription)
    setZone(nextZone)
    setStartDate(nextStart)
    setEndDate(nextEnd)
    const docPreview = payload.draft?.metadata?.documentationPreview
    const nextRubros = (payload.draft?.subprojects || []).map((sp, idx) => {
      const inherited = sp.datesInheritedFromProject === true
      let s = sp.startDate || (inherited ? nextStart : '') || ''
      let e = sp.endDate || (inherited ? nextEnd : '') || ''
      if (nextEnd) {
        if (s) s = clampIsoDateToMax(s, nextEnd)
        if (e) e = clampIsoDateToMax(e, nextEnd)
      }
      if (s && e && e < s) e = s
      const tid = sp.tempId || `rubro-${idx}`
      const slotsFromSp = normalizeDocumentationSlots(sp.documentationSlots)
      let documentationSlots = slotsFromSp
      let sourcePagesNorm = normalizePageList(sp.sourcePages)
      const assignmentForRubro = docPreview?.assignments?.find(
        (x) => String(x?.tempId ?? '') === String(tid)
      )
      if (assignmentForRubro && !documentationSlotsHavePages(slotsFromSp)) {
        documentationSlots = normalizeDocumentationSlots(assignmentForRubro.documentationSlots)
        if (!sourcePagesNorm?.length) {
          sourcePagesNorm = normalizePageList(assignmentForRubro.sourcePages)
        }
      }
      const matchedSkillNameFromBack =
        String(sp.matchedSkillName || '').trim() ||
        String(assignmentForRubro?.matchedSkillName || '').trim() ||
        undefined
      return {
        tempId: tid,
        detectedLabel: sp.detectedLabel || '',
        selectedSkillId: sp.selectedSkillId,
        description: sp.description || '',
        status: (sp.status as 'OPEN' | 'CLOSE') || 'OPEN',
        skillMatchKind: sp.skillMatchKind ?? assignmentForRubro?.skillMatchKind,
        matchedSkillName: matchedSkillNameFromBack,
        matchScore: sp.matchScore,
        name: sp.name,
        link: sp.link,
        startDate: s,
        endDate: e,
        datesInheritedFromProject: sp.datesInheritedFromProject,
        documentationSlots,
        sourcePages: sourcePagesNorm,
        ...(() => {
          const dfIds = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})
          const hasDf = DOCUMENTATION_SLOT_KEYS.some((k) => !!dfIds[k])
          return hasDf ? { documentationFileIds: dfIds } : {}
        })()
      }
    })
    setRubros(nextRubros)
    setFuzzyConfirmByRubro({})
    setPatchedDraftSync(null)
    committedRef.current = {
      name: nextName,
      description: nextDescription,
      zone: nextZone,
      startDate: nextStart,
      endDate: nextEnd,
      rubrosKey: serializeRubrosSnapshot(nextRubros),
      rubrosCount: nextRubros.length
    }
    setSaveState('idle')
    setExpandedRubros((prev) => {
      const next: Record<string, boolean> = {}
      nextRubros.forEach((r) => {
        if (prev[r.tempId]) next[r.tempId] = true
      })
      return next
    })
  }, [
    payload.draft?.project?.name,
    payload.draft?.project?.description,
    payload.draft?.project?.zone,
    payload.draft?.project?.startDate,
    payload.draft?.project?.endDate,
    payload.draft?.subprojects,
    payload.draft?.metadata?.documentationPreview
  ])

  useEffect(() => {
    let mounted = true
    const mapReduxSkills = (list: unknown[]): SkillOption[] =>
      (list || [])
        .map((skill: any) => ({
          id: Number(skill.id),
          name: String(skill.name || '').trim()
        }))
        .filter((skill: SkillOption) => !!skill.id && !!skill.name)
    const load = async () => {
      try {
        const skills = await profesionalesService.fetchSkillCatalog()
        if (!mounted) return
        setSkillOptions(skills)
      } catch {
        if (!mounted) return
        setSkillOptions(mapReduxSkills(cachedSkillsRef.current || []))
      }
    }
    void load()
    return () => {
      mounted = false
    }
  }, [])

  const draftId = payload.draftId || payload.collectedData?.draftId || payload.draft?.id

  useEffect(() => {
    setPublishSuccess(null)
  }, [draftId])

  useEffect(() => {
    setMetadataFilesOverride(null)
  }, [draftId])

  const serverMetadataFiles = useMemo((): DraftMetadataFileEntry[] => {
    const raw = payload.draft?.metadata?.files
    return Array.isArray(raw) ? (raw as DraftMetadataFileEntry[]) : []
  }, [payload.draft?.metadata?.files])

  const metadataFiles = metadataFilesOverride ?? serverMetadataFiles

  const patchMetadataPayload = useCallback(() => {
    const m = shallowMergeDraftMetadataForPatch(payload.draft?.metadata)
    const effective = metadataFilesOverride ?? serverMetadataFiles
    if (effective.length > 0) {
      m.files = effective
    }
    return m
  }, [payload.draft?.metadata, metadataFilesOverride, serverMetadataFiles])

  const mergedCollectedData =
    patchedDraftSync?.collectedData != null
      ? { ...payload.collectedData, ...patchedDraftSync.collectedData }
      : payload.collectedData
  const requiredFields = mergedCollectedData?.requiredFields ?? EMPTY_REQUIRED_FIELDS
  const requiredFieldsWithZone = useMemo(
    () => Array.from(new Set([...requiredFields, 'project.zone'])),
    [requiredFields]
  )
  const mergedDraftValidation = useMemo(
    () =>
      mergeProjectAutofillDraftValidation({
        patched: patchedDraftSync?.validation,
        payload: payload.validation,
        draft: payload.draft?.validation
      }),
    [patchedDraftSync?.validation, payload.validation, payload.draft?.validation]
  )
  const { warnings, publicationReadiness, blockingIssues: mergedBlockingIssues } = mergedDraftValidation
  const serverBlockingIssuesNonEmpty = (mergedBlockingIssues ?? []).some(
    (x) => String(x || '').trim() !== ''
  )
  const publishedProyectoId = String(
    payload.draft?.metadata?.publicationResult?.proyectoId ?? ''
  ).trim()
  const alreadyPublishedFromMetadata = publishedProyectoId.length > 0
  const draftLocked = !!publishSuccess || alreadyPublishedFromMetadata
  const nameSource = String(payload.draft?.metadata?.nameSource || '').toLowerCase()
  const descriptionSourceNorm = String(payload.draft?.metadata?.descriptionSource || '').toLowerCase()
  const showSuggestedNameBadge = nameSource === 'rubro' || nameSource === 'default'
  const showSuggestedDescriptionBadge =
    descriptionSourceNorm === 'rubro' ||
    descriptionSourceNorm === 'default' ||
    descriptionSourceNorm === 'document' ||
    descriptionSourceNorm === 'content' ||
    descriptionSourceNorm === 'extracted'
  const endDateSourceNorm = String(payload.draft?.metadata?.endDateSource || '').toLowerCase()
  const showSuggestedEndDateFromMeta =
    !!endDateSourceNorm && endDateSourceNorm !== 'user' && endDateSourceNorm !== 'manual'
  const showSuggestedEndDateHint =
    showSuggestedEndDateFromMeta || warnings.some((w) => warningMatchesSuggestedEndDate(String(w || '')))
  const showSuggestedNameHint =
    showSuggestedNameBadge || warnings.some((w) => warningMatchesSuggestedName(String(w || '')))
  const showSuggestedDescriptionHint =
    showSuggestedDescriptionBadge ||
    warnings.some((w) => warningMatchesSuggestedDescription(String(w || '')))
  const discardedPastProjectDates = payload.draft?.metadata?.discardedPastProjectDates === true
  const documentationPreview = payload.draft?.metadata?.documentationPreview
  const docPdfUrl = documentationPreview?.primaryStorageUrl?.trim() || ''
  const docPageCount = getEffectiveDocumentationPageCount(
    documentationPreview,
    payload.draft?.metadata
  )
  const docPageValidationHint = useMemo(() => {
    if (docPageCount == null) return null
    return validateRubrosDocumentationPages(rubros, docPageCount)
  }, [docPageCount, rubros])
  const docStatus = String(documentationPreview?.status || '').trim()
  const docIsPreview = docStatus === 'pending_user_review'
  const showSuggestedDocumentationHint =
    docIsPreview || warnings.some((w) => warningMatchesSuggestedDocumentation(String(w || '')))
  const showGlobalDocBar =
    documentationPreview != null &&
    (docIsPreview || docPageCount != null || !!docPdfUrl)
  const documentExtractionMeta = payload.draft?.metadata?.documentExtraction
  const analysisErrorRaw = String(payload.draft?.metadata?.analysisError || '').trim()
  const docExtractParseError = String(documentExtractionMeta?.parseError || '').trim()
  const docExtractOcrError = String(documentExtractionMeta?.ocrError || '').trim()
  const docTechnicalDetail = [docExtractParseError, docExtractOcrError].filter(Boolean).join('\n\n')
  const draftProject = payload.draft?.project
  const subprojects = rubros

  const buildSubprojectsPayload = useCallback(
    (rows: RubroRow[]) =>
      mapRubrosToDraftPayload(rows, (id) => skillOptions.find((s) => s.id === id)?.name),
    [skillOptions]
  )

  const editorValidation = useMemo(
    () =>
      computeProjectAutofillEditorUiState({
        validation: mergedDraftValidation,
        requiredFields,
        requiredFieldsWithZone,
        draftProject,
        name,
        description,
        zone,
        startDate,
        endDate,
        rubros
      }),
    [
      mergedDraftValidation,
      requiredFields,
      requiredFieldsWithZone,
      draftProject,
      name,
      description,
      zone,
      startDate,
      endDate,
      rubros
    ]
  )

  const highlightField = (field: string) => editorValidation.highlightedFields.has(field)

  const today = new Date().toISOString().slice(0, 10)

  const committed = committedRef.current
  const rubrosKeyNow = serializeRubrosSnapshot(rubros)
  const projectDirty =
    !!committed &&
    (name !== committed.name ||
      description !== committed.description ||
      zone !== committed.zone ||
      startDate !== committed.startDate ||
      endDate !== committed.endDate)
  const rubrosDirty = !!committed && rubrosKeyNow !== committed.rubrosKey
  const dirty = projectDirty || rubrosDirty

  const fuzzyNeedsConfirm = rubros.some(
    (r) => isRubroFuzzyKind(r.skillMatchKind) && !fuzzyConfirmByRubro[r.tempId]
  )

  const anyRubroNeedsMasterPdf = useMemo(() => rubros.some(rubroNeedsMasterPdfForCrop), [rubros])

  const highlightedRubroTempIds = useMemo(() => {
    const ids = new Set<string>()
    rubros.forEach((sp, idx) => {
      const k = rubroKey(sp, idx)
      if (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ids.add(k)
      if (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId) ids.add(k)
      if (isRubroFuzzyKind(sp.skillMatchKind) && !fuzzyConfirmByRubro[sp.tempId]) ids.add(k)
      if (rubroNeedsMasterPdfForCrop(sp) && !docPdfUrl.trim()) ids.add(k)
    })
    if (docPageCount != null) {
      for (const tid of getRubroTempIdsWithDocumentationPageIssues(rubros, docPageCount)) {
        ids.add(tid)
      }
    }
    const msgMatched = matchValidationMessagesToRubroTempIds(
      [...editorValidation.filteredBlockingIssues, ...editorValidation.warningsNotHandledInline],
      rubros,
      skillOptions
    )
    msgMatched.forEach((tid) => ids.add(tid))
    if (
      editorValidation.highlightedFields.has('subprojects') &&
      rubros.length === 1 &&
      ids.size === 0
    ) {
      ids.add(rubroKey(rubros[0], 0))
    }
    return ids
  }, [
    rubros,
    fuzzyConfirmByRubro,
    docPageCount,
    docPdfUrl,
    editorValidation.filteredBlockingIssues,
    editorValidation.warningsNotHandledInline,
    editorValidation.highlightedFields,
    skillOptions
  ])

  const advisoryNotesByRubroKey = useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const raw of editorValidation.advisoryWarnings) {
      const matched = matchValidationMessagesToRubroTempIds([raw], rubros, skillOptions)
      Array.from(matched).forEach((tid) => {
        if (!out[tid]) out[tid] = []
        if (!out[tid].includes(raw)) out[tid].push(raw)
      })
    }
    return out
  }, [editorValidation.advisoryWarnings, rubros, skillOptions])

  const unmatchedAdvisoryWarnings = useMemo(
    () =>
      editorValidation.advisoryWarnings.filter(
        (raw) => matchValidationMessagesToRubroTempIds([raw], rubros, skillOptions).size === 0
      ),
    [editorValidation.advisoryWarnings, rubros, skillOptions]
  )

  const highlightSubprojectsSection =
    highlightedRubroTempIds.size === 0 &&
    ((rubros.length === 0 && highlightField('subprojects')) ||
      !!docPageValidationHint ||
      (anyRubroNeedsMasterPdf && !docPdfUrl.trim()))

  const applyUploadedFilesList = useCallback(
    async (res: unknown): Promise<DraftMetadataFileEntry[] | null> => {
      let next = extractDraftMetadataFilesFromResponse(res)
      if ((!next || next.length === 0) && draftId) {
        try {
          const fresh = await getProjectAutofillDraft(String(draftId))
          next = extractDraftMetadataFilesFromResponse(fresh) ?? null
        } catch {
        }
      }
      if (next && next.length > 0) {
        setMetadataFilesOverride(next)
        return next
      }
      return null
    },
    [draftId]
  )

  const handleSelectRubroSlotFileId = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, fileId: string | null) => {
      setRubros((prev) =>
        prev.map((r) => {
          if (r.tempId !== tempId) return r
          const cur = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
          const next = { ...cur }
          if (fileId && fileId.trim()) {
            next[slot] = fileId.trim()
            const slotsCur = normalizeDocumentationSlots(r.documentationSlots ?? {})
            const nextSlots: DocumentationSlots = { ...slotsCur }
            delete nextSlots[slot]
            const hasAnySlots = documentationSlotsHavePages(nextSlots)
            const hasAny = DOCUMENTATION_SLOT_KEYS.some((k) => !!next[k])
            return {
              ...r,
              documentationFileIds: hasAny ? next : undefined,
              documentationSlots: hasAnySlots ? nextSlots : {}
            }
          }
          delete next[slot]
          const hasAny = DOCUMENTATION_SLOT_KEYS.some((k) => !!next[k])
          return { ...r, documentationFileIds: hasAny ? next : undefined }
        })
      )
      setSaveState('idle')
    },
    []
  )

  const handleRubroSlotPagesChange = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, pages: number[]) => {
      setRubros((prev) =>
        prev.map((r) => {
          if (r.tempId !== tempId) return r
          const cur = normalizeDocumentationSlots(r.documentationSlots ?? {})
          const norm = normalizePageList(pages)
          const nextSlots: DocumentationSlots = { ...cur }
          if (norm?.length) nextSlots[slot] = norm
          else delete nextSlots[slot]
          const hasAny = documentationSlotsHavePages(nextSlots)
          return { ...r, documentationSlots: hasAny ? nextSlots : {} }
        })
      )
      setSaveState('idle')
    },
    []
  )

  const handleUploadRubroSlotFile = useCallback(
    async (tempId: string, slot: keyof DocumentationSlots, file: File) => {
      if (!draftId) return
      const key = `${tempId}:${slot}`
      setUploadingSlotKey(key)
      setSaveState('idle')
      try {
        const prevList = metadataFiles
        const res = await postProjectAutofillDraftFile(String(draftId), file)
        const next = await applyUploadedFilesList(res)
        if (next?.length) {
          const newId = pickNewFileIdAfterUpload(prevList, next)
          if (newId) {
            handleSelectRubroSlotFileId(tempId, slot, newId)
          }
        }
      } catch {
        setSaveState('error')
      } finally {
        setUploadingSlotKey(null)
      }
    },
    [draftId, metadataFiles, applyUploadedFilesList, handleSelectRubroSlotFileId]
  )

  const applyPatchResponseToValidation = useCallback(async (res: unknown, id: string) => {
    let bundle = validationBundleFromPatchResponse(res)
    if (!bundle) {
      try {
        const fresh = await getProjectAutofillDraft(id)
        bundle = validationBundleFromPatchResponse(fresh)
      } catch {
      }
    }
    if (bundle) {
      setPatchedDraftSync(bundle)
    }
  }, [])

  const patchZoneAfterPlaceSelect = useCallback(
    async (address: string) => {
      if (!draftId || !address.trim()) return
      try {
        const res = await patchProjectAutofillDraft(String(draftId), {
          project: { zone: address.trim() },
          subprojects: buildSubprojectsPayload(rubros),
          metadata: patchMetadataPayload()
        })
        await applyPatchResponseToValidation(res, String(draftId))
      } catch {
      }
    },
    [draftId, rubros, applyPatchResponseToValidation, patchMetadataPayload, buildSubprojectsPayload]
  )

  const uploadBusy = uploadingSlotKey != null
  const canSaveDraft = useMemo(() => {
    if (!draftId) return false
    if (isSaving) return false
    if (!dirty) return false
    if (rubros.length < 1) return false
    if (fuzzyNeedsConfirm) return false
    if (docPageValidationHint) return false
    if (uploadBusy) return false
    if (draftLocked) return false
    if (editorValidation.pendingFields.length > 0) return false
    if (editorValidation.filteredBlockingIssues.length > 0) return false
    if (anyRubroNeedsMasterPdf && !docPdfUrl.trim()) return false
    if (!rubros.every((r) => rubroHasValidSelectedSkill(r))) return false
    return true
  }, [
    draftId,
    isSaving,
    dirty,
    rubros,
    fuzzyNeedsConfirm,
    docPageValidationHint,
    uploadBusy,
    draftLocked,
    editorValidation,
    anyRubroNeedsMasterPdf,
    docPdfUrl
  ])

  const performSave = useCallback(async (): Promise<boolean> => {
    if (!draftId || !dirty || fuzzyNeedsConfirm || uploadingSlotKey != null) return false
    if (rubros.length < 1) return false
    if (draftLocked) return false
    if (editorValidation.pendingFields.length > 0) return false
    if (editorValidation.filteredBlockingIssues.length > 0) return false
    if (anyRubroNeedsMasterPdf && !docPdfUrl.trim()) return false
    if (!rubros.every((r) => rubroHasValidSelectedSkill(r))) return false
    const maxPage = getEffectiveDocumentationPageCount(
      documentationPreview,
      payload.draft?.metadata
    )
    if (maxPage != null) {
      const verr = validateRubrosDocumentationPages(rubros, maxPage)
      if (verr) {
        setSaveState('error')
        return false
      }
    }
    setIsSaving(true)
    setSaveState('idle')
    try {
      const patchBody = {
        project: {
          name: name || undefined,
          description: description.trim() ? description : undefined,
          zone: zone.trim() ? zone : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined
        },
        subprojects: buildSubprojectsPayload(rubros),
        metadata: patchMetadataPayload()
      }
      const res = await patchProjectAutofillDraft(String(draftId), patchBody)
      await applyPatchResponseToValidation(res, String(draftId))
      committedRef.current = {
        name,
        description,
        zone,
        startDate,
        endDate,
        rubrosKey: serializeRubrosSnapshot(rubros),
        rubrosCount: rubros.length
      }
      setSaveState('ok')
      return true
    } catch {
      setSaveState('error')
      return false
    } finally {
      setIsSaving(false)
    }
  }, [
    draftId,
    dirty,
    fuzzyNeedsConfirm,
    name,
    description,
    zone,
    startDate,
    endDate,
    rubros,
    applyPatchResponseToValidation,
    documentationPreview,
    payload.draft?.metadata,
    patchMetadataPayload,
    uploadingSlotKey,
    draftLocked,
    editorValidation,
    anyRubroNeedsMasterPdf,
    docPdfUrl,
    buildSubprojectsPayload
  ])

  const performSaveRef = useRef(performSave)
  performSaveRef.current = performSave

  const canPublishDraft = useMemo(() => {
    if (!draftId) return false
    if (rubros.length < 1) return false
    if (!rubros.every((r) => rubroHasValidSelectedSkill(r))) return false
    if (serverBlockingIssuesNonEmpty) return false
    if (publicationReadiness?.readyForCommitSequence !== true) return false
    if (alreadyPublishedFromMetadata) return false
    if (dirty) return false
    if (fuzzyNeedsConfirm) return false
    if (docPageValidationHint) return false
    if (uploadBusy) return false
    if (isSaving) return false
    if (isPublishing) return false
    if (publishSuccess) return false
    if (draftLocked) return false
    return true
  }, [
    draftId,
    rubros,
    serverBlockingIssuesNonEmpty,
    publicationReadiness?.readyForCommitSequence,
    alreadyPublishedFromMetadata,
    dirty,
    fuzzyNeedsConfirm,
    docPageValidationHint,
    uploadBusy,
    isSaving,
    isPublishing,
    publishSuccess,
    draftLocked
  ])
  const publishSnapRef = useRef({ canPublishDraft, isPublishing })
  publishSnapRef.current = { canPublishDraft, isPublishing }

  const handlePublish = useCallback(async () => {
    if (!checkAccess('publish_private_project')) return
    if (!draftId || !canPublishDraft || rubros.length < 1) return
    setPublishError(null)
    setIsPublishing(true)
    try {
      const res = await publishProjectAutofillDraft(String(draftId))
      const parsed = parsePublishProjectAutofillDraftResponse(res)
      const pid = parsed.proyectoId?.trim()
      if (!pid) {
        setPublishError('No se recibió el id del proyecto.')
        return
      }
      setPublishSuccess({
        proyectoId: pid,
        obraName: name.trim(),
        subproyectoCount: parsed.subproyectoIds?.length ?? rubros.length
      })
    } catch (e) {
      setPublishError(formatPublishProjectAutofillDraftError(e))
    } finally {
      setIsPublishing(false)
    }
  }, [checkAccess, draftId, canPublishDraft, name, rubros.length])

  const handlePublishRef = useRef(handlePublish)
  handlePublishRef.current = handlePublish

  const guardSnapRef = useRef({
    dirty,
    draftId: draftId as string | undefined,
    fuzzyNeedsConfirm,
    isSaving,
    uploadBusy: false,
    rubrosCount: rubros.length,
    canSaveDraft: false
  })
  guardSnapRef.current = {
    dirty,
    draftId,
    fuzzyNeedsConfirm,
    isSaving,
    uploadBusy,
    rubrosCount: rubros.length,
    canSaveDraft
  }

  useEffect(() => {
    if (!guardSetApi) {
      return
    }
    if (!draftId) {
      guardSetApi(null)
      return
    }
    const api: ProjectAutofillDraftGuardApi = {
      isDirty: () => guardSnapRef.current.dirty,
      canPersist: () => guardSnapRef.current.canSaveDraft,
      saveDraft: () => performSaveRef.current(),
      canPublish: () => publishSnapRef.current.canPublishDraft,
      publishDraft: async () => {
        await handlePublishRef.current()
      },
      isPublishing: () => publishSnapRef.current.isPublishing
    }
    guardSetApi(api)
    return () => {
      guardSetApi(null)
    }
  }, [guardSetApi, draftId, canPublishDraft, isPublishing, canSaveDraft])

  useEffect(() => {
    guardBumpUi?.()
  }, [guardBumpUi, canPublishDraft, isPublishing, publishSuccess, alreadyPublishedFromMetadata, draftLocked])

  const handleSave = () => {
    void performSave()
  }

  const toggleRubroExpanded = (tempId: string) => {
    setExpandedRubros((prev) => ({ ...prev, [tempId]: !prev[tempId] }))
  }

  const updateRubro = (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => {
    setRubros((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, ...updates } : r)))
    setSaveState('idle')
  }

  const applyRubroSkillChange = useCallback(
    (tempId: string, selectedSkill: SkillOption, clearDocumentation: boolean) => {
      setRubros((prev) =>
        prev.map((r) => {
          if (r.tempId !== tempId) return r
          const next: RubroRow = {
            ...r,
            detectedLabel: selectedSkill.name,
            selectedSkillId: selectedSkill.id,
            matchedSkillName: selectedSkill.name
          }
          if (clearDocumentation) {
            next.documentationSlots = {}
            next.sourcePages = undefined
            next.documentationFileIds = undefined
          }
          return next
        })
      )
      setFuzzyConfirmByRubro((prev) => ({ ...prev, [tempId]: false }))
      setSaveState('idle')
    },
    []
  )

  const handleRubroSkillSelect = (tempId: string, selectedValue: string, currentRow: RubroRow) => {
    if (draftLocked) return
    if (!selectedValue) {
      updateRubro(tempId, { selectedSkillId: undefined, detectedLabel: '', matchedSkillName: undefined })
      setFuzzyConfirmByRubro((prev) => ({ ...prev, [tempId]: false }))
      return
    }
    const skillId = Number(selectedValue)
    const selectedSkill = skillOptions.find((skill) => skill.id === skillId)
    if (!selectedSkill) return
    if (currentRow.selectedSkillId === selectedSkill.id) return
    if (rubroHasDocumentationToReset(currentRow)) {
      setPendingRubroSkillChange({ tempId, skill: selectedSkill })
      return
    }
    applyRubroSkillChange(tempId, selectedSkill, false)
  }

  const rubroPrimaryDisplayName = (sp: RubroRow): string => {
    if (rubroHasValidSelectedSkill(sp)) {
      const byId = skillOptions.find((s) => s.id === sp.selectedSkillId)
      if (byId) return byId.name
    }
    const matched = sp.matchedSkillName?.trim()
    if (matched) return matched
    const detected = sp.detectedLabel?.trim()
    if (detected) return detected
    return ''
  }

  const rubroHeadline = (sp: RubroRow) => {
    const name = rubroPrimaryDisplayName(sp)
    if (name) return name
    return 'Elegí el rubro a cotizar'
  }

  const rubroUnresolvedNeedsPick = (sp: RubroRow) =>
    isRubroUnresolvedKind(sp.skillMatchKind) && !rubroHasValidSelectedSkill(sp)

  const createObraDetailModalTarget = useMemo(() => {
    if (!isCreateObraShell || createObraRubroModalRid == null) return null
    const idx = rubros.findIndex((r) => r.tempId === createObraRubroModalRid)
    if (idx < 0) return null
    const rubroRow = rubros[idx]
    return { rubro: rubroRow, rk: rubroKey(rubroRow, idx), rid: createObraRubroModalRid }
  }, [isCreateObraShell, rubros, createObraRubroModalRid])

  useEffect(() => {
    if (!createObraRubroModalRid) return
    if (!rubros.some((r) => r.tempId === createObraRubroModalRid)) {
      setCreateObraRubroModalRid(null)
    }
  }, [rubros, createObraRubroModalRid])

  const handleAddRubro = () => {
    if (draftLocked) return
    const tid = `rubro-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    setRubros((prev) => [
      ...prev,
      {
        tempId: tid,
        detectedLabel: '',
        description: '',
        status: 'OPEN',
        datesInheritedFromProject: true,
        startDate: startDate || '',
        endDate: endDate || '',
        manualAdd: true
      }
    ])
    setExpandedRubros((prev) => ({ ...prev, [tid]: true }))
    setFuzzyConfirmByRubro((prev) => ({ ...prev, [tid]: false }))
    setSaveState('idle')
    if (isCreateObraShell) {
      setCreateObraRubroModalRid(tid)
    }
  }

  const handleRemoveRubro = (tempId: string) => {
    if (draftLocked) return
    setRubros((prev) => prev.filter((r) => r.tempId !== tempId))
    setExpandedRubros((prev) => {
      const next = { ...prev }
      delete next[tempId]
      return next
    })
    if (createObraRubroModalRid === tempId) {
      setCreateObraRubroModalRid(null)
    }
    setSaveState('idle')
  }

  const renderSaveFooter = (opts?: { rail?: boolean }) => {
    if (!draftId) return null
    return (
      <div
        className={[
          styles.projectAutofillSaveFooter,
          opts?.rail ? styles.projectAutofillSaveFooterCreateObra : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {dirty && (
          <p className={styles.projectAutofillUnsavedNote}>
            Tenés cambios sin guardar. Pulsa Guardar borrador para fijarlos en el servidor y actualizar la validación (incluye rubros).
          </p>
        )}
        <div className={styles.projectAutofillSaveFooterBar}>
          <div className={styles.projectAutofillSaveStatus}>
            {saveState === 'ok' && (
              <span className={styles.projectAutofillSaveOk}>Guardado</span>
            )}
            {saveState === 'error' && (
              <span className={styles.projectAutofillSaveError}>Error al guardar</span>
            )}
          </div>
          <div className={styles.projectAutofillSaveButtons}>
            <button
              type="button"
              className={styles.projectAutofillSaveDraftButton}
              onClick={handleSave}
              disabled={!canSaveDraft}
            >
              {isSaving ? 'Guardando...' : 'Guardar borrador'}
            </button>
            {publishSuccess ? null : alreadyPublishedFromMetadata ? (
              <Link
                to={`/obrasprivadas/${encodeURIComponent(publishedProyectoId)}`}
                className={styles.projectAutofillPublishedLink}
                title={PUBLISHED_RUBRO_EDIT_HINT}
              >
                Ver obra publicada
              </Link>
            ) : (
              <button
                type="button"
                className={draftPublishStyles.draftPublishButton}
                onClick={() => void handlePublish()}
                disabled={!canPublishDraft || isPublishing}
              >
                {isPublishing ? (
                  <span>Publicando...</span>
                ) : (
                  <>
                    <Rocket size={18} strokeWidth={1.8} aria-hidden />
                    <span>Publicar obra</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        {publishError && <p className={styles.projectAutofillPublishError}>{publishError}</p>}
        {publishSuccess && (
          <PublishedPrivateProjectCard
            proyectoId={publishSuccess.proyectoId}
            obraName={publishSuccess.obraName}
            subproyectoCount={publishSuccess.subproyectoCount}
          />
        )}
      </div>
    )
  }

  return (
    <div
      className={[
        styles.projectAutofillCard,
        isCreateObraShell ? styles.projectAutofillCardCreateObra : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {!isCreateObraShell && (
        <div className={styles.projectAutofillHeader}>
          <div className={styles.projectAutofillHeaderLead}>
            <span className={styles.projectAutofillTitle}>Borrador de obra</span>
            {publicationReadiness?.readyForCommitSequence && (
              <span
                className={styles.projectAutofillReadyMark}
                title="Listo para publicar"
                aria-label="Listo para publicar"
              >
                <Check size={17} strokeWidth={2.5} aria-hidden />
              </span>
            )}
          </div>
          <span className={styles.projectAutofillStatus}>
            {humanizeDraftStatus(
              mergedCollectedData?.draftStatus ?? payload.draft?.status ?? payload.collectedData?.draftStatus
            )}
          </span>
        </div>
      )}
      {analysisErrorRaw && (
        <div className={styles.projectAutofillAnalysisNote}>{analysisErrorRaw}</div>
      )}
      {docTechnicalDetail && (
        <div className={styles.projectAutofillDocExtract}>
          <details className={styles.projectAutofillDocExtractDetails}>
            <summary className={styles.projectAutofillDocExtractDetailsSummary}>Detalle técnico</summary>
            <pre className={styles.projectAutofillDocExtractDetailsBody}>{docTechnicalDetail}</pre>
          </details>
        </div>
      )}
      {discardedPastProjectDates && (
        <div className={styles.projectAutofillMetadataNote}>
          Las fechas del documento eran anteriores a hoy; ajustamos el calendario del proyecto a fechas
          vigentes. Revisá inicio y fin.
        </div>
      )}
      {draftLocked && (
        <p className={styles.projectAutofillLockedHint}>Este borrador ya no se puede editar.</p>
      )}
      <div
        className={
          draftLocked
            ? styles.projectAutofillDraftLockedWrap
            : embedLayout === 'createObraDesktop'
              ? `${styles.projectAutofillEditorWrap} ${styles.projectAutofillEditorWrapCreateObraSplit}`
              : styles.projectAutofillEditorWrap
        }
      >
      <div
        className={
          isCreateObraShell
            ? styles.projectAutofillCreateObraFormShell
            : styles.projectAutofillEditorOuterPassThrough
        }
      >
        {isCreateObraShell && (
          <div className={styles.projectAutofillCreateObraFormHeader}>
            <h2 className={styles.projectAutofillCreateObraFormTitle}>Datos del proyecto</h2>
            <p className={styles.projectAutofillCreateObraFormSubtitle}>
              Completá la información para comenzar a recibir cotizaciones
            </p>
          </div>
        )}
      <div className={styles.projectAutofillEditor}>
        <div className={styles.projectAutofillField}>
          <div className={styles.projectAutofillFieldLabelRow}>
            <span className={styles.projectAutofillFieldLabel}>
              {isCreateObraShell ? 'Nombre del Proyecto' : 'Nombre de la obra'}
            </span>
            {showSuggestedNameHint && (
              <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
            )}
          </div>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setSaveState('idle')
            }}
            placeholder="Ej. Ampliación cocina"
            disabled={draftLocked}
            aria-invalid={highlightField('project.name') || undefined}
            style={highlightField('project.name') ? PENDING_INPUT_STYLE : undefined}
          />
        </div>
        <div className={`${styles.projectAutofillField} ${styles.projectAutofillPlacesWrap}`}>
          <span className={styles.projectAutofillFieldLabel}>Ubicación de la obra</span>
          <PlacesAutocomplete
            value={zone}
            onChange={(v) => {
              setZone(v)
              setSaveState('idle')
            }}
            onPlaceSelect={({ address }) => {
              patchZoneAfterPlaceSelect(address)
            }}
            placeholder="Ej. Palermo, CABA"
            embed="messageDraft"
            hideLabel
            className={styles.projectAutofillPlaces}
            outlineError={highlightField('project.zone')}
            outlineErrorStyle="draft"
            disabled={draftLocked}
          />
        </div>
        <div className={styles.projectAutofillField}>
          <span className={styles.projectAutofillFieldLabel}>Fecha de inicio</span>
          <p className={styles.projectAutofillHint}>
            Día en que tu proyecto empieza a publicarse en Edify. Durante ese periodo podes sumar rubros para recibir cotizaciones; no es el inicio de la obra en sitio.
          </p>
          <input
            type="date"
            value={startDate}
            min={today}
            onChange={(e) => {
              const nextStart = e.target.value
              const nextProjEnd = endDate && nextStart && endDate < nextStart ? nextStart : endDate
              setStartDate(nextStart)
              if (endDate && nextStart && endDate < nextStart) {
                setEndDate(nextStart)
              }
              setRubros((prev) =>
                prev.map((r) => {
                  if (r.datesInheritedFromProject === true) {
                    return {
                      ...r,
                      startDate: nextStart,
                      endDate: nextProjEnd || r.endDate
                    }
                  }
                  let s = r.startDate
                  let ed = r.endDate
                  if (nextStart && s && s < nextStart) s = nextStart
                  if (nextStart && ed && ed < nextStart) ed = nextStart
                  if (nextProjEnd) {
                    if (s) s = clampIsoDateToMax(s, nextProjEnd)
                    if (ed) ed = clampIsoDateToMax(ed, nextProjEnd)
                  }
                  if (s && ed && ed < s) ed = s
                  if (s === r.startDate && ed === r.endDate) return r
                  return { ...r, startDate: s, endDate: ed, datesInheritedFromProject: false }
                })
              )
              setSaveState('idle')
            }}
            disabled={draftLocked}
            aria-invalid={highlightField('project.startDate') || undefined}
            style={highlightField('project.startDate') ? PENDING_INPUT_STYLE : undefined}
          />
        </div>
        <div className={styles.projectAutofillField}>
          <div className={styles.projectAutofillFieldLabelRow}>
            <span className={styles.projectAutofillFieldLabel}>Fecha de finalización</span>
            {showSuggestedEndDateHint && (
              <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
            )}
          </div>
          <p className={styles.projectAutofillHint}>
            Hasta cuando permanece publicado en Edify. Mientras tanto podes seguir agregando rubros; no representa el fin de la obra en sitio.
          </p>
          <input
            type="date"
            value={endDate}
            min={startDate || today}
            onChange={(e) => {
              const nextEnd = e.target.value
              setEndDate(nextEnd)
              setRubros((prev) =>
                prev.map((r) => {
                  if (r.datesInheritedFromProject === true) {
                    return {
                      ...r,
                      startDate: startDate,
                      endDate: nextEnd
                    }
                  }
                  let s = r.startDate
                  let ed = r.endDate
                  if (nextEnd) {
                    if (s) s = clampIsoDateToMax(s, nextEnd)
                    if (ed) ed = clampIsoDateToMax(ed, nextEnd)
                  }
                  if (s && ed && ed < s) ed = s
                  if (s === r.startDate && ed === r.endDate) return r
                  return { ...r, startDate: s, endDate: ed, datesInheritedFromProject: false }
                })
              )
              setSaveState('idle')
            }}
            disabled={draftLocked}
            aria-invalid={highlightField('project.endDate') || undefined}
            style={highlightField('project.endDate') ? PENDING_INPUT_STYLE : undefined}
          />
        </div>
        {(payload.draft?.project?.description !== undefined ||
          description.trim() !== '' ||
          requiredFields.includes('project.description')) && (
            <div className={styles.projectAutofillField}>
              <div className={styles.projectAutofillFieldLabelRow}>
                <span className={styles.projectAutofillFieldLabel}>Descripción de la obra</span>
                {showSuggestedDescriptionHint && (
                  <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
                )}
              </div>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  setSaveState('idle')
                }}
                placeholder="Detalle de la obra, materiales, alcance..."
                rows={4}
                disabled={draftLocked}
                className={styles.projectAutofillProjectDescription}
                aria-invalid={highlightField('project.description') || undefined}
                style={highlightField('project.description') ? PENDING_INPUT_STYLE : undefined}
              />
            </div>
          )}
      </div>
      </div>
      <div
        className={[
          styles.projectAutofillSubprojects,
          highlightSubprojectsSection ? styles.projectAutofillSubprojectsHighlight : '',
          embedLayout === 'createObraDesktop' ? styles.projectAutofillSubprojectsCreateObraRail : '',
          isCreateObraShell ? sm.subProjectManager : ''
        ]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={highlightSubprojectsSection || undefined}
      >
        <div
          className={
            isCreateObraShell
              ? styles.projectAutofillCreateObraRailColumn
              : styles.projectAutofillSubprojectsPassThrough
          }
        >
          <div
            className={
              isCreateObraShell
                ? styles.projectAutofillCreateObraRailScroll
                : styles.projectAutofillSubprojectsPassThrough
            }
          >
        {isCreateObraShell ? (
          <div className={styles.createObraRubrosHub}>
            <div className={styles.createObraRubrosHubIntro}>
              <div className={sm.headerMain}>
                <div className={sm.headerText}>
                  <h3 className={sm.title}>Rubros a cotizar</h3>
                  <p className={sm.subtitle}>Agregá los trabajos que necesitás cotizar</p>
                </div>
                {showGlobalDocBar ? (
                  <div className={sm.infoButtonWrap} ref={createObraPdfInfoRef}>
                    <button
                      type="button"
                      className={sm.infoButton}
                      aria-expanded={createObraPdfInfoOpen}
                      aria-haspopup="dialog"
                      aria-label="Información sobre el documento analizado"
                      onClick={() => setCreateObraPdfInfoOpen((o) => !o)}
                    >
                      <Info size={16} aria-hidden />
                    </button>
                    {createObraPdfInfoOpen ? (
                      <div
                        className={sm.infoPopover}
                        role="dialog"
                        aria-label="Documento analizado"
                      >
                        <button
                          type="button"
                          className={sm.infoPopoverClose}
                          onClick={() => setCreateObraPdfInfoOpen(false)}
                          aria-label="Cerrar"
                        >
                          ×
                        </button>
                        <h4 className={sm.infoPopoverTitle}>Documento analizado</h4>
                        <p className={sm.infoPopoverIntro}>
                          El PDF analizado es la base de este borrador: sumá rubros manualmente o reemplazá el
                          documento para volver a detectarlos automáticamente.
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            {subprojects.length === 0 && !draftLocked && (
              <p className={styles.createObraRubrosHubCallout}>
                Todavía no hay rubros en el proyecto. Usá «Agregar rubro» en la lista o, si acabás de subir un
                documento, esperá a que termine el análisis.
              </p>
            )}
            {showGlobalDocBar ? (
              <div className={styles.createObraRubrosHubStatus} role="status">
                <p className={styles.createObraRubrosHubStatComment}>
                  {docPageCount != null
                    ? docPageCount === 1
                      ? '1 página relevada en el documento'
                      : `${docPageCount} páginas relevadas en el documento`
                    : 'Documento procesado'}
                </p>
                <div className={styles.createObraRubrosHubStatusLinks}>
                  {docPdfUrl ? (
                    <a
                      href={docPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.createObraRubrosHubStatLink}
                    >
                      Abrir PDF
                    </a>
                  ) : (
                    <span className={styles.createObraRubrosHubStatPending}>
                      Preparando enlace al PDF
                    </span>
                  )}
                  {!draftLocked && draftId && onRequestChangeSourceDocument ? (
                    <>
                      <span className={styles.createObraRubrosHubStatSep} aria-hidden>
                        ·
                      </span>
                      <button
                        type="button"
                        className={styles.createObraRubrosHubStatusDocAction}
                        onClick={onRequestChangeSourceDocument}
                      >
                        <FileUp size={15} strokeWidth={1.75} aria-hidden />
                        Cambiar documentación
                      </button>
                    </>
                  ) : null}
                </div>
                {!draftLocked && draftId && !onRequestChangeSourceDocument ? (
                  <p className={styles.createObraRubrosHubStatusDocHint}>
                    Para analizar otro PDF, volvé al inicio del asistente.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className={styles.projectAutofillSubprojectsToolbar}>
            <span className={styles.projectAutofillSubprojectsTitle}>
              Rubros a cotizar en la obra ({subprojects.length})
            </span>
            {!draftLocked && (
              <button
                type="button"
                className={styles.projectAutofillAddRubroButton}
                onClick={handleAddRubro}
              >
                <Plus size={16} />
                Agregar rubro
              </button>
            )}
          </div>
        )}
        {unmatchedAdvisoryWarnings.map((text, i) => (
          <p key={`adv-${i}`} className={styles.projectAutofillRubroAdvisoryNote} role="note">
            <span className={styles.projectAutofillRubroAdvisoryBadge}>Revisar</span>
            {text}
          </p>
        ))}
        {draftLocked && (
          <p className={styles.projectAutofillRubrosLockedHint}>{PUBLISHED_RUBRO_EDIT_HINT}</p>
        )}
        {!isCreateObraShell && subprojects.length === 0 && !draftLocked && (
          <p className={styles.projectAutofillRubrosEmptyHint}>
            No hay rubros cargados. Agregá al menos uno para guardar el borrador y publicar la obra.
          </p>
        )}
        {showGlobalDocBar && !isCreateObraShell ? (
          <div className={styles.projectAutofillDocReceived}>
            <div className={styles.projectAutofillDocReceivedRow}>
              <div className={styles.projectAutofillDocReceivedTitleGroup}>
                <span className={styles.projectAutofillDocReceivedTitle}>Documentación recibida</span>
                {showSuggestedDocumentationHint && (
                  <span className={styles.projectAutofillFieldSuggested}>Sugerido</span>
                )}
              </div>
              {docPdfUrl ? (
                <a
                  href={docPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.projectAutofillDocReceivedLink}
                >
                  Ver documentación recibida
                </a>
              ) : (
                <span className={styles.projectAutofillDocReceivedPending}>
                  El enlace aparecerá cuando el archivo esté listo
                </span>
              )}
            </div>
            {(() => {
              const metaParts: string[] = []
              if (docPageCount != null) {
                metaParts.push(
                  docPageCount === 1
                    ? '1 página analizada'
                    : `${docPageCount} páginas analizadas`
                )
              }
              if (metaParts.length === 0) return null
              return (
                <p className={styles.projectAutofillDocReceivedMeta}>{metaParts.join('. ')}</p>
              )
            })()}
            <p className={styles.projectAutofillDocReceivedRollback}>
              ¿No es el documento correcto? Enviá otro archivo en el chat del agente para analizarlo de nuevo y
              actualizar esta propuesta.
            </p>
          </div>
        ) : null}
        {(isCreateObraShell ? (
          <div
            className={[
              sm.subProjectsListScroll,
              styles.projectAutofillCreateObraSubProjectsListScroll
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={sm.subProjectsList}>
              <div className={sm.subProjectsListInner}>
                <div className={styles.createObraRubrosListToolbar}>
                  <span className={styles.createObraRubrosListCounter} aria-live="polite">
                    {subprojects.length} rubro{subprojects.length !== 1 ? 's' : ''}
                  </span>
                  {!draftLocked && draftId ? (
                    <button
                      type="button"
                      className={[sm.addButton, styles.createObraRubrosListToolbarAdd].join(' ')}
                      onClick={handleAddRubro}
                      disabled={isSaving}
                    >
                      <Plus size={18} />
                      Agregar rubro
                    </button>
                  ) : null}
                </div>
                {subprojects.map((sp, idx) => {
                  const rk = rubroKey(sp, idx)
                  const rid = sp.tempId || `sp-${idx}`
                  return (
                    <div
                      key={rk}
                      className={[
                        sm.subProjectItem,
                        highlightedRubroTempIds.has(rk) ? sm.invalid : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className={sm.subProjectMain}>
                        <div className={sm.subProjectContent}>
                          <div className={sm.subProjectHeader}>
                            {rubroUnresolvedNeedsPick(sp) ? (
                              <div
                                className={styles.projectAutofillSubprojectLabelBlock}
                                style={{ flex: 1, minWidth: 0 }}
                              >
                                <span className={styles.projectAutofillRubroUnresolvedTitle}>
                                  Se detectó un rubro en el documento
                                </span>
                                <span className={styles.projectAutofillRubroUnresolvedLine}>
                                  No encontramos el nombre correcto en la plataforma.
                                </span>
                                <span className={styles.projectAutofillRubroUnresolvedLineMuted}>
                                  Elegí el rubro en la lista.
                                </span>
                              </div>
                            ) : (
                              <h4 className={sm.subProjectName}>{rubroHeadline(sp)}</h4>
                            )}
                            <div className={sm.subProjectActions}>
                              <button
                                type="button"
                                className={sm.actionButton}
                                onClick={() => setCreateObraRubroModalRid(rid)}
                                disabled={!draftId || isSaving || draftLocked}
                                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : 'Editar rubro'}
                              >
                                <Edit3 size={16} />
                              </button>
                              <button
                                type="button"
                                className={`${sm.actionButton} ${sm.deleteButton}`}
                                onClick={() => handleRemoveRubro(rid)}
                                disabled={!draftId || isSaving || draftLocked}
                                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : 'Eliminar rubro'}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          subprojects.map((sp, idx) => {
          const rk = rubroKey(sp, idx)
          const rid = sp.tempId || `sp-${idx}`
          return (
          <div
            key={rk}
            className={
              highlightedRubroTempIds.has(rk)
                ? `${styles.projectAutofillSubprojectCard} ${styles.projectAutofillSubprojectCardHighlight}`
                : styles.projectAutofillSubprojectCard
            }
          >
            <div className={styles.projectAutofillSubprojectHead}>
              <button
                type="button"
                className={styles.projectAutofillSubprojectToggle}
                aria-expanded={!!expandedRubros[rid]}
                onClick={() => toggleRubroExpanded(rid)}
              >
                {expandedRubros[rid] ? 'Ocultar' : 'Ver detalle'}
              </button>
              {rubroUnresolvedNeedsPick(sp) ? (
                <div className={styles.projectAutofillSubprojectLabelBlock}>
                  <span className={styles.projectAutofillRubroUnresolvedTitle}>
                    Se detectó un rubro en el documento
                  </span>
                  <span className={styles.projectAutofillRubroUnresolvedLine}>
                    No encontramos el nombre correcto en la plataforma.
                  </span>
                  <span className={styles.projectAutofillRubroUnresolvedLineMuted}>
                    Elegí el rubro en la lista.
                  </span>
                </div>
              ) : (
                <span className={styles.projectAutofillSubprojectLabel}>{rubroHeadline(sp)}</span>
              )}
              <button
                type="button"
                className={styles.projectAutofillSubprojectRemove}
                onClick={() => handleRemoveRubro(rid)}
                disabled={!draftId || isSaving || draftLocked}
                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
              >
                Quitar
              </button>
            </div>
            {advisoryNotesByRubroKey[rk]?.map((text, i) => (
              <p key={`${rk}-adv-${i}`} className={styles.projectAutofillRubroAdvisoryNote} role="note">
                <span className={styles.projectAutofillRubroAdvisoryBadge}>Revisar</span>
                {text}
              </p>
            ))}
            {expandedRubros[rid] && (
              <div
                className={`${styles.projectAutofillSubprojectBody} ${styles.projectAutofillSubprojectBodyEnter}`}
              >
                {!rubroUnresolvedNeedsPick(sp) &&
                  (sp.matchedSkillName || sp.matchScore != null) && (
                  <div className={styles.projectAutofillMatchHint}>
                    {sp.matchedSkillName && (
                      <span>Coincidencia sugerida: {sp.matchedSkillName}</span>
                    )}
                    {sp.matchScore != null && (
                      <span className={styles.projectAutofillMatchScore}>
                        Puntaje: {typeof sp.matchScore === 'number' ? sp.matchScore.toFixed(2) : sp.matchScore}
                      </span>
                    )}
                  </div>
                )}
                <div className={styles.projectAutofillRubroSelect}>
                  <CustomSelect
                    value={sp.selectedSkillId ? String(sp.selectedSkillId) : ''}
                    onChange={(value) => handleRubroSkillSelect(sp.tempId || `sp-${idx}`, value, sp)}
                    label={
                      rubroUnresolvedNeedsPick(sp) ? 'Rubro de la plataforma' : 'Rubro a cotizar'
                    }
                    outlineInvalid={
                      (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ||
                      (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId)
                    }
                    options={skillOptions.map((skill) => ({
                      value: String(skill.id),
                      label: skill.name
                    }))}
                    placeholder={
                      rubroUnresolvedNeedsPick(sp)
                        ? 'Elegí el rubro de la lista'
                        : 'Elegir rubro de la lista'
                    }
                    searchable={true}
                    searchPlaceholder="Buscar rubro..."
                    disabled={draftLocked}
                    title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                  />
                </div>
                <textarea
                  value={sp.description || ''}
                  onChange={(e) => updateRubro(sp.tempId || `sp-${idx}`, { description: e.target.value })}
                  placeholder="Describe brevemente el trabajo a realizar..."
                  disabled={draftLocked}
                  title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                />

                {(() => {
                  const slots = normalizeDocumentationSlots(sp.documentationSlots)
                  const idsNorm = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})
                  const hasSlotFileId = DOCUMENTATION_SLOT_KEYS.some((k) => !!idsNorm[k])
                  const showRubroDoc =
                    documentationPreview != null ||
                    documentationSlotsHavePages(slots) ||
                    (sp.sourcePages?.length ?? 0) > 0 ||
                    hasSlotFileId ||
                    (draftId != null && metadataFiles.length > 0) ||
                    showGlobalDocBar
                  if (!showRubroDoc) return null
                  const rid = sp.tempId || `sp-${idx}`
                  return (
                    <RubroDocumentationBlock
                      tempId={rid}
                      slots={slots}
                      sourcePages={sp.sourcePages}
                      pdfBaseUrl={docPdfUrl || undefined}
                      maxPage={docPageCount ?? undefined}
                      fileIds={idsNorm}
                      draftFiles={metadataFiles}
                      disabled={!draftId || isSaving || draftLocked}
                      title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                      uploadingSlotKey={uploadingSlotKey}
                      onSelectSlotFileId={(slot, fileId) =>
                        handleSelectRubroSlotFileId(rid, slot, fileId)
                      }
                      onSlotPagesChange={
                        docPageCount != null && draftId && !isSaving && !draftLocked
                          ? (slot, pages) => handleRubroSlotPagesChange(rid, slot, pages)
                          : undefined
                      }
                      onUploadSlotFile={(slot, file) =>
                        handleUploadRubroSlotFile(rid, slot, file)
                      }
                    />
                  )
                })()}

                {(sp.name || sp.link) && (
                  <div className={styles.projectAutofillSubprojectExtras}>
                    {sp.name && (
                      <span>
                        Nombre: {sp.name}
                      </span>
                    )}
                    {sp.link && (
                      <a href={sp.link} target="_blank" rel="noopener noreferrer" className={styles.projectAutofillSubprojectLink}>
                        Enlace
                      </a>
                    )}
                  </div>
                )}

                {isRubroFuzzyKind(sp.skillMatchKind) && (
                  <label className={styles.projectAutofillFuzzyConfirm}>
                    <input
                      type="checkbox"
                      checked={!!fuzzyConfirmByRubro[sp.tempId || `sp-${idx}`]}
                      disabled={draftLocked}
                      title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                      onChange={(e) =>
                        setFuzzyConfirmByRubro((prev) => ({
                          ...prev,
                          [sp.tempId || `sp-${idx}`]: e.target.checked
                        }))
                      }
                    />
                    <span>Confirmo que el rubro elegido es el correcto</span>
                  </label>
                )}
                <div className={styles.projectAutofillRubroDates}>
                  {sp.datesInheritedFromProject === true && (
                    <span className={styles.projectAutofillInheritedDatesHint}>Mismas fechas que el proyecto</span>
                  )}
                  <label className={styles.projectAutofillRubroDateLabel}>
                    <span>Fecha inicio de recepción de presupuestos</span>
                    <input
                      type="date"
                      value={sp.startDate || ''}
                      min={startDate || today}
                      max={endDate || undefined}
                      title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                      onChange={(e) => {
                        const rid = sp.tempId || `sp-${idx}`
                        let nextStart = e.target.value
                        if (endDate && nextStart) nextStart = clampIsoDateToMax(nextStart, endDate)
                        let nextEnd = sp.endDate
                        if (endDate && nextEnd) nextEnd = clampIsoDateToMax(nextEnd, endDate)
                        if (nextEnd && nextStart && nextEnd < nextStart) {
                          nextEnd = nextStart
                        }
                        updateRubro(rid, {
                          startDate: nextStart,
                          endDate: nextEnd,
                          datesInheritedFromProject: false
                        })
                      }}
                      disabled={draftLocked}
                    />
                  </label>
                  <label className={styles.projectAutofillRubroDateLabel}>
                    <span>Fecha fin de recepción de presupuestos</span>
                    <input
                      type="date"
                      value={sp.endDate || ''}
                      min={sp.startDate || startDate || today}
                      max={endDate || undefined}
                      title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                      onChange={(e) => {
                        let v = e.target.value
                        if (endDate && v) v = clampIsoDateToMax(v, endDate)
                        const rid = sp.tempId || `sp-${idx}`
                        const s = sp.startDate
                        if (s && v && v < s) v = s
                        updateRubro(rid, {
                          endDate: v,
                          datesInheritedFromProject: false
                        })
                      }}
                      disabled={draftLocked}
                    />
                  </label>
                </div>

                <select
                  value={sp.status || 'OPEN'}
                  disabled={draftLocked}
                  title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                  onChange={(e) => updateRubro(sp.tempId || `sp-${idx}`, { status: e.target.value as 'OPEN' | 'CLOSE' })}
                >
                  <option value="OPEN">Abierto</option>
                  <option value="CLOSE">Cerrado</option>
                </select>
              </div>
            )}
          </div>
          )
        })
        )
        )}
          </div>
          {isCreateObraShell ? renderSaveFooter({ rail: true }) : null}
        </div>
      </div>
      </div>
      {isCreateObraShell ? (
        <ProjectAutofillRubroDetailModal
          isOpen={createObraDetailModalTarget != null}
          title={
            createObraDetailModalTarget ? rubroHeadline(createObraDetailModalTarget.rubro) : ''
          }
          rubro={createObraDetailModalTarget?.rubro ?? null}
          tempId={createObraDetailModalTarget?.rid ?? ''}
          skillOptions={skillOptions}
          draftLocked={draftLocked}
          draftId={draftId}
          isSaving={isSaving}
          uploadingSlotKey={uploadingSlotKey}
          documentationPreview={documentationPreview}
          showGlobalDocBar={showGlobalDocBar}
          docPdfUrl={docPdfUrl}
          docPageCount={docPageCount}
          metadataFiles={metadataFiles}
          today={today}
          projectStartDate={startDate}
          projectEndDate={endDate}
          advisoryNotes={
            createObraDetailModalTarget
              ? advisoryNotesByRubroKey[createObraDetailModalTarget.rk] ?? []
              : []
          }
          onClose={() => setCreateObraRubroModalRid(null)}
          handleRubroSkillSelect={handleRubroSkillSelect}
          updateRubro={updateRubro}
          handleSelectRubroSlotFileId={handleSelectRubroSlotFileId}
          handleRubroSlotPagesChange={handleRubroSlotPagesChange}
          handleUploadRubroSlotFile={handleUploadRubroSlotFile}
          fuzzyConfirmByRubro={fuzzyConfirmByRubro}
          setFuzzyConfirmByRubro={setFuzzyConfirmByRubro}
          rubroUnresolvedNeedsPick={rubroUnresolvedNeedsPick}
          clampIsoDateToMax={clampIsoDateToMax}
        />
      ) : null}
      <ModalOverlay
        isOpen={!!pendingRubroSkillChange}
        onClose={() => setPendingRubroSkillChange(null)}
        zIndex={10}
      >
        <div className={styles.projectAutofillConfirmDialog}>
          <div className={styles.projectAutofillConfirmDialogHeader}>
            <h2 className={styles.projectAutofillConfirmDialogTitle}>Cambiar rubro</h2>
            <button
              type="button"
              className={styles.projectAutofillConfirmDialogClose}
              onClick={() => setPendingRubroSkillChange(null)}
              aria-label="Cerrar"
            >
              <X size={20} strokeWidth={2} aria-hidden />
            </button>
          </div>
          <p className={styles.projectAutofillConfirmDialogText}>
            Podés mantener la documentación vinculada (páginas sugeridas del documento y archivos cargados
            por tipo) o quitarla por completo.
          </p>
          <div className={styles.projectAutofillConfirmDialogActions}>
            <button
              type="button"
              className={styles.projectAutofillConfirmDialogClearDoc}
              onClick={() => {
                const p = pendingRubroSkillChange
                if (!p) return
                applyRubroSkillChange(p.tempId, p.skill, true)
                setPendingRubroSkillChange(null)
              }}
            >
              Cambiar rubro y quitar documentación
            </button>
            <button
              type="button"
              className={styles.projectAutofillConfirmDialogPrimary}
              onClick={() => {
                const p = pendingRubroSkillChange
                if (!p) return
                applyRubroSkillChange(p.tempId, p.skill, false)
                setPendingRubroSkillChange(null)
              }}
            >
              Cambiar rubro y mantener documentación
            </button>
          </div>
        </div>
      </ModalOverlay>
      {!isCreateObraShell ? renderSaveFooter() : null}
    </div>
  )
}

export default ProjectAutofillCard
